
import { GlobalContext } from "@/model/globalcontext.model";
import { GoogleMedia } from "@/model/googlemedia.model";
import { GuideLink } from "@/model/guide.link.model";
import { PlayerBar } from "@/model/playerbar.model";
import { Playlist } from "@/model/playlist.model";
import { Release } from "@/model/release.model";
import FilesystemCustomUtils from "@/services/filesystem.custom.utils";
import GestureService from "@/services/gesture.service";
import LogoService from "@/services/logo.service";
import MediaUtils from "@/services/media.utils";
import UnicodeUtils from "@/services/unicode.utils";
import { ScreenOrientation } from "@awesome-cordova-plugins/screen-orientation";
import { Capacitor, CapacitorHttp } from "@capacitor/core";
import {
  Directory,
  Encoding,
  FileInfo,
  Filesystem,
} from "@capacitor/filesystem";
import { Preferences } from "@capacitor/preferences";
import {
  IonButton,
  IonContent,
  IonIcon,
  IonImg,
  IonItem,
  IonLabel,
  IonPage,
  IonPopover,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonToggle,
  isPlatform,
} from "@ionic/vue";
import axios from "axios";
import { RmxAudioPlayer } from "capacitor-plugin-playlist";
import {
  addCircle,
  arrowUpOutline,
  ban,
  chevronDown,
  close,
  menu,
  pause,
  play,
  playBack,
  playForward,
  playSkipBack,
  playSkipForward,
  removeCircle,
  resizeOutline,
  settings,
} from "ionicons/icons";
import { defineComponent, inject, ref } from "vue";
// @ts-ignore
import { MittEmitter } from "mitt-emitter";

let backdropTimeout: ReturnType<typeof setTimeout>;
let rewindTimeout: ReturnType<typeof setTimeout>;
let rewindClickTimeout: ReturnType<typeof setTimeout>;

const __default__ = defineComponent({
  name: "ReleaseVideo",
  components: {
    IonContent,
    IonPage,
    IonSelect,
    IonSelectOption,
    IonIcon,
    IonItem,
    IonToggle,
    IonLabel,
    IonButton,
    IonSpinner,
    IonPopover,
    IonImg,
  },

  setup() {
    const videoRef = ref();
    const pdfContainer = ref();
    const pdfViewerRef = ref();
    const pdfContainerRef = ref();

    const globalContext = inject("globalContext") as GlobalContext;
    const isMobile = globalContext.isMobile;
    const releaseEntity = inject("release") as Release;
    const playerBar = inject("playerBar") as PlayerBar;
    const audioPlayer = inject("audioPlayer") as RmxAudioPlayer;
    const emitter = inject("emitter") as MittEmitter;
    const fsUtils = new FilesystemCustomUtils();
    const unicodeUtils = new UnicodeUtils();
    const mediaUtils = new MediaUtils();
    const gestureService = new GestureService();

    const selectOptions: any = {
      cssClass: "video-options",
    };

    const mimeExtensions: Map<string, string> = new Map([
      ["image/jpeg", "jpg"],
      ["image/png", "png"],
      ["application/json", "json"],
      ["audio/mpeg", "mp3"],
      ["video/mp4", "mp4"],
      ["video/mpeg", "mpeg"],
      ["video/quicktime", "mov"],
      ["application/pdf", "pdf"],
    ]);

    const videoQualityDic: Map<string, string> = new Map([
      ["lq", "Низкое"],
      ["hq", "Высокое"]
    ]);

    return {
      videoRef,
      pdfContainer,
      pdfViewerRef,
      pdfContainerRef,
      selectOptions,
      releaseEntity,
      playerBar,
      play,
      pause,
      playSkipBack,
      playSkipForward,
      ban,
      resizeOutline,
      chevronDown,
      settings,
      close,
      addCircle,
      removeCircle,
      mimeExtensions,
      videoQualityDic,
      emitter,
      fsUtils,
      unicodeUtils,
      isMobile,
      mediaUtils,
      globalContext,
      playBack,
      playForward,
      gestureService,
      arrowUpOutline,
      menu,
      audioPlayer,
    };
  },

  data() {
    return {
      isPipMode: false,
      pipModeAvailable: false,
      isPlayBack: false,
      isPlayForward: false,
      pdfData: "",
      videoList: Array<GoogleMedia>(),
      guideList: Array<GoogleMedia>(),
      guidePath: "",
      guideToken: "",
      trackPath: "",
      // Track which videos are downloaded locally (for hybrid online/offline mode)
      localVideos: new Map<string, boolean>(),
      currentVideoId: "",
      currentVideoIdx: 0,
      currentVideoName: "",
      isFullScreen: false,
      videoMirrorMode: false,
      openVideoSettings: false,
      videoPlaying: false,
      continuePlaying: false,
      videoWaiting: false,
      showBackdrop: false,
      buffered: "0%",
      videoPlaybackRate: 1.0,
      videoQuality: "hq",
      playerRangeMaxValue: 0,
      playerRangeCurrentValue: 0,
      currentTrackTotalTime: "00:00",
      currentTrackCurrentTime: "00:00",
      isDragPlayerRange: false,
      selectedPage: 1,
      programLogo: "",
      logoService: new LogoService(),
      guideNumPages: -1,
      guidePages: Array<FileInfo>(),
      guideNotFound: false,
      lastGuidePosition: {
        x: 0,
        y: 0,
        scale: 1,
      },
      links: Array<GuideLink>(),
      showScrollTop: false,
      guideNumPagesVisible: Array<number>(),
      showLinksContainer: true,
      expandLinks: false,
      rewindClicks: 0,
    };
  },

  ionViewWillEnter() {
    this.loadProgramLogo();
    if (document.pictureInPictureElement) {
      document
        .exitPictureInPicture()
        .then(() => console.log("Document Exited from Picture-in-Picture mode"))
        .catch((err) => console.error(err));
    } else {
      this.playerBar.visible = false;
      this.playerBar.microMode = false;
      this.showBackdrop = true;
      this.videoList = [];
      this.guideList = [];
      this.currentVideoIdx = 0;
      this.currentVideoId = "";
      this.currentVideoName = "";
      this.guidePath = "";
      this.trackPath = "";
      this.videoWaiting = true;

      try {
        this.getVideoList();
        this.loadGuideAsImages();
      } catch (err) {
        this.videoWaiting = false;
      }
    }
  },

  ionViewWillLeave() {
    // return portrait mode. Actual for Android back button
    if (this.isMobile) {
      ScreenOrientation.lock(ScreenOrientation.ORIENTATIONS.PORTRAIT_PRIMARY);

      if (this.videoPlaying) {
        this.setPipMode();
      }
    } else {
      if (!document.pictureInPictureElement) {
        this.videoRef.pause();
      }
    }
  },

  mounted() {
    this.$nextTick(() => {
      if (!this.isMobile) this.isFullScreen = false;
      else {
        this.isFullScreen =
          ScreenOrientation.type ==
          ScreenOrientation.ORIENTATIONS.LANDSCAPE ||
          ScreenOrientation.type ==
          ScreenOrientation.ORIENTATIONS.LANDSCAPE_PRIMARY ||
          ScreenOrientation.type ==
          ScreenOrientation.ORIENTATIONS.LANDSCAPE_SECONDARY;


          ScreenOrientation.onChange().subscribe(() => {
            if (!isPlatform("ipad") && !isPlatform("tablet")) {
              this.isFullScreen =
                  ScreenOrientation.type == ScreenOrientation.ORIENTATIONS.LANDSCAPE ||
                  ScreenOrientation.type ==
                  ScreenOrientation.ORIENTATIONS.LANDSCAPE_PRIMARY ||
                  ScreenOrientation.type ==
                  ScreenOrientation.ORIENTATIONS.LANDSCAPE_SECONDARY;
            }
          });
      }

      this.videoRef.addEventListener("ended", () => {
        if (document.pictureInPictureElement) {
          this.exitPipMode();
        }
        this.nextTrack();
      });

      this.videoRef.addEventListener("playing", () => {
        this.videoPlaying = true;
        if (this.videoWaiting) {
          this.videoWaiting = false;
          this.showBackdrop = false;
        }
      });

      this.videoRef.addEventListener("play", () => {
        setTimeout(() => {
          if (!this.videoWaiting) {
            this.showBackdrop = false;
          }
        }, 2000);
      });

      this.videoRef.addEventListener("pause", () => {
        this.videoPlaying = false;
        this.showBackdrop = true;
      });

      this.videoRef.addEventListener("loadstart", () => {
        if (this.videoQuality === "lq") {
          setTimeout(() => {
            if (!this.videoPlaying) {
              this.videoQuality = "hq";
              this.restartVideoPlayer();
            }
          }, 10000);
        }
      });

      this.videoRef.addEventListener("loadedmetadata", () => {
        this.buffered = "0%";
        if (this.videoRef && this.videoRef.duration) {
          this.currentTrackTotalTime = this.mediaUtils.formatTime(
            this.videoRef.duration
          );
          this.playerRangeMaxValue = Math.floor(this.videoRef.duration);
        }
        if (this.videoRef && this.videoRef.currentTime) {
          this.currentTrackCurrentTime = this.mediaUtils.formatTime(
            this.videoRef.currentTime
          );
        }
        if (this.continuePlaying) {
          this.videoRef.play();
        }
        if (document.pictureInPictureEnabled) {
          this.pipModeAvailable = true;
        }
      });

      this.videoRef.addEventListener("canplaythrough", () => {
        this.videoWaiting = false;
      });

      this.videoRef.addEventListener("progress", () => {
        if (this.videoRef && this.videoRef.buffered && this.videoRef.buffered.length > 0 && this.videoRef.duration) {
          this.buffered = `${(this.videoRef.buffered.end(0) / this.videoRef.duration) * 100}%`;
        }
      });

      this.videoRef.addEventListener("timeupdate", () => {
        if (!this.isDragPlayerRange && this.videoRef) {
          this.currentTrackCurrentTime = this.mediaUtils.formatTime(
            this.videoRef.currentTime
          );
          this.playerRangeCurrentValue = this.videoRef.currentTime;
        }
      });

      this.videoRef.addEventListener("error", () => {
        this.videoQuality = "hq";
        this.restartVideoPlayer();
      });
    });
  },

  beforeUnmount() {
    // Set videoRef to null to prevent errors in event listeners
    if (this.videoRef) {
      this.videoRef.pause();
      this.videoRef = null;
    }
  },

  watch: {
    guideNumPages(newVal: number) {
      if (newVal > 0) {
        this.$nextTick(() => {
          const guideWrapper = document.getElementById("pdf-video-wrapper");
          const parent = document.getElementById("pdf-video-container");
          if (guideWrapper) {
            this.gestureService.enablePinchZoom(
              guideWrapper as HTMLElement,
              parent as HTMLElement
            );

            guideWrapper.onscroll = () => {
              this.showScrollTop = guideWrapper.scrollTop > 400;
            };
          }
        });
      }
    },
  },

  methods: {
    onRewindClick(rewindValue: number) {
      this.rewindClicks++;
      if (this.rewindClicks === 1) {
        if (this.showBackdrop) {
          this.changeCurrentTime(rewindValue);
        }
        this.showControls();
        rewindClickTimeout = setTimeout(() => {
          this.rewindClicks = 0
        }, 700);
      } else {    
          this.changeCurrentTime(rewindValue);
          clearTimeout(rewindClickTimeout);
          this.rewindClicks = 0;
      }
    },
    setLinksContainerVisibility() {
      this.showLinksContainer = !this.showLinksContainer;
    },
    scrollTop(pageNum?: any): void {
      const guideWrapper = document.getElementById("pdf-video-wrapper");
      if (guideWrapper && !pageNum) {
        guideWrapper.scrollTo({
          top: 0,
          left: 0,
          behavior: "smooth",
        });
      } else if (pageNum) {
        const destPage = document.getElementById(
          `video-guide-page-${pageNum ? Number(pageNum) + 1 : 2}`
        );
        if (guideWrapper && destPage) {
          guideWrapper.scrollTo({
            top: destPage.offsetTop,
            left: 0,
            behavior: "smooth",
          });
        }
      }
    },

    async loadGuideAsImages() {
      // Check if release uses server storage
      if (this.releaseEntity.contentSource === 'SERVER') {
        // Use server API
        if (this.releaseEntity.guideOffline) {
          // Load offline guide
          console.log("Found offline directory: " + this.releaseEntity.guideDirectory);

          Filesystem.readdir({
            path: this.releaseEntity.guideDirectory,
            directory: Directory.Data,
          })
            .then((response) => {
              Filesystem.getUri({
                path: `${this.releaseEntity.guideDirectory}`,
                directory: Directory.Data,
              }).then((res) => {
                this.guidePath = `${Capacitor.convertFileSrc(res.uri)}`;

                this.guidePages = response.files
                  .filter((f) => {
                    return f.name.endsWith("png");
                  })
                  .sort((a, b) => {
                    let sorted = 0;
                    if (a.name < b.name) sorted = -1;
                    else if (a.name > b.name) sorted = 1;
                    return sorted;
                  });
                this.guideNumPages = this.guidePages.length;

                const guideLinks = response.files.find((f) => {
                  return f.name === this.fsUtils.GUIDE_LINKS_PATH;
                });

                if (undefined !== guideLinks) {
                  Filesystem.readFile({
                    path: `${this.releaseEntity.guideDirectory}/${guideLinks.name}`,
                    directory: Directory.Data,
                    encoding: Encoding.UTF8,
                  }).then((linksResponse) => {
                    try {
                      this.links = JSON.parse(typeof linksResponse.data === 'string' ? linksResponse.data : '');
                      this.releaseEntity.links = this.links;
                    } catch (ex) {
                      console.error("Error extracting links", ex);
                    }
                  });
                }
              });
            })
            .catch(() => {
              this.guideNumPages = 0;
            });
        } else {
          // Load from server API
          const tokenQuery = await Preferences.get({ key: "jwt" });
          const token = tokenQuery.value;

          const options = {
            url: `${process.env.VUE_APP_API_URL}api/releases/${this.releaseEntity.id}/guide/pages`,
            headers: { Authorization: `Bearer ${token}` },
            readTimeout: 1000000,
          };

          await CapacitorHttp.get(options)
            .then((response) => {
              // Store base path and token separately
              this.guidePath = `${process.env.VUE_APP_API_URL}api/releases/${this.releaseEntity.id}/guide/pages`;
              this.guideToken = token || '';
              this.guideNumPages = Number(response.data.pages);
              this.releaseEntity.guideNumPages = this.guideNumPages;
            })
            .catch(() => {
              console.error("Failed to load guide");
              this.guideNumPages = 0;
            });
        }
        return;
      }

      // Original Google Drive logic (for backward compatibility)
      if (this.releaseEntity.guideOffline) {
        //load offline guide
        console.log(
          "Found offline directory: " + this.releaseEntity.guideDirectory
        );

        Filesystem.readdir({
          path: this.releaseEntity.guideDirectory,
          directory: Directory.Data,
        })
          .then((response) => {
            Filesystem.getUri({
              path: `${this.releaseEntity.guideDirectory}`,
              directory: Directory.Data,
            }).then((res) => {
              this.guidePath = `${Capacitor.convertFileSrc(res.uri)}`;

              this.guidePages = response.files
                .filter((f) => {
                  return f.name.endsWith("png");
                })
                .sort((a, b) => {
                  let sorted = 0;
                  if (a.name < b.name) sorted = -1;
                  else if (a.name > b.name) sorted = 1;
                  return sorted;
                });
              this.guideNumPages = this.guidePages.length;

              const guideLinks = response.files.find((f) => {
                return f.name === this.fsUtils.GUIDE_LINKS_PATH;
              });

              if (undefined !== guideLinks) {
                Filesystem.readFile({
                  path: `${this.releaseEntity.guideDirectory}/${guideLinks.name}`,
                  directory: Directory.Data,
                  encoding: Encoding.UTF8,
                }).then((linksResponse) => {
                  try {
                    this.links = JSON.parse(typeof linksResponse.data === 'string' ? linksResponse.data : '');
                    this.releaseEntity.links = this.links;
                  } catch (ex) {
                    console.error("Error extracting links", ex);
                  }
                });
              }
            });
          })
          .catch(() => {
            // not exists or not a dir
            this.guideNumPages = 0;
          });
      } else {
        const tokenQuery = await Preferences.get({ key: "jwt" });
        const token = tokenQuery.value;

        const options = {
          url: `${process.env.VUE_APP_API_URL}api/google/media/${this.releaseEntity.programShortName +
            "_" +
            this.releaseEntity.releaseWithZero +
            "_Guide"
            }`,
          headers: { Authorization: `Bearer ${token}` },
          readTimeout: 1000000,
        };

        await CapacitorHttp.get(options)
          .then((response) => {
            this.guideList = response.data;

            this.guidePath =
              this.guideList.length > 0
                ? `${process.env.VUE_APP_API_EXT_URL}api/google/guide/pages/${this.guideList[0].id}`
                : "";

            if (this.releaseEntity.guideNumPages == 0) {
              const pagesOptions = {
                url: `${process.env.VUE_APP_API_EXT_URL}api/google/guide/pages-with-links/${this.guideList[0].id}`,
                headers: { Authorization: `Bearer ${token}` },
                readTimeout: 1000000,
              };

              CapacitorHttp.get(pagesOptions).then((response) => {
                try {
                  if (response.data.links) {
                    this.links = JSON.parse(response.data.links);
                    this.releaseEntity.links = this.links;
                  }
                } catch (ex) {
                  console.error("Error extracting links", ex);
                } finally {
                  this.guideNumPages = Number(response.data.pages);
                  this.releaseEntity.guideNumPages = this.guideNumPages;
                }
              });
            } else {
              this.guideNumPages = this.releaseEntity.guideNumPages;
              this.links = this.releaseEntity.links;
            }
          })
          .catch(() => {
            console.error("Failed to load guide");
            this.guideNumPages = 0;
          });
      }
    },

    showVideoSettings() {
      this.openVideoSettings = true;
    },

    setMirrorMode(event: CustomEvent) {
      this.videoMirrorMode = event.detail.checked;
    },

    setPipMode() {
      this.openVideoSettings = false;
      this.isPipMode = true;
      this.videoRef.requestPictureInPicture();
    },

    exitPipMode() {
      document.exitPictureInPicture();
    },

    changeVideoRate(event: CustomEvent) {
      this.videoPlaybackRate = Number(event.detail.value);
      this.videoRef.playbackRate = this.videoPlaybackRate * 1.0;
    },

    changeVideoQuality(event: CustomEvent) {
      this.videoQuality = String(event.detail.value);
      this.restartVideoPlayer();
    },

    openWebFullScreen() {
      ScreenOrientation.unlock();

      if (this.videoRef.requestFullscreen) {
        this.videoRef.requestFullscreen();
      } else if (this.videoRef.webkitRequestFullscreen) {
        /* Safari */
        this.videoRef.webkitRequestFullscreen();
      } else if (this.videoRef.msRequestFullscreen) {
        /* IE11 */
        this.videoRef.msRequestFullscreen();
      }
    },

    formatVideoName(name: string) {
      if (!name) return '';
      else {
        const pattern = /(\d{2})_(.*?)\.mp4/;
        const match = name.match(pattern);

        if (match) {
          const prefix = parseInt(match[1], 10);
          name = match[2];
          if (prefix > 0) {
            name = `${prefix} - ${name}`;
          }
        }
      }

      return name;
    },

    async getVideoList() {
      // Check if release uses server storage
      if (this.releaseEntity.contentSource === 'SERVER') {
        // Use server API
        if (this.isMobile) {
          if (this.globalContext.offlineMode) {
            // Load offline video from local playlist
            console.log("Offline mode: Loading videos from local playlist");

            Filesystem.readdir({
              path: this.releaseEntity.videoDirectory,
              directory: Directory.Data,
            })
              .then((res) => {
                const foundPlaylist = res.files.find((f) => {
                  return f.name == this.fsUtils.PLAYLIST_PATH;
                });

                if (typeof foundPlaylist !== "undefined" && null != foundPlaylist) {
                  Filesystem.readFile({
                    path: `${this.releaseEntity.videoDirectory}/${this.fsUtils.PLAYLIST_PATH}`,
                    directory: Directory.Data,
                    encoding: Encoding.UTF8,
                  })
                    .then((res) => {
                      const localPlaylist: Playlist = typeof res.data === 'string' ? JSON.parse(res.data) : null;
                      if (!localPlaylist) throw new Error("Local playlist is null");

                      this.videoList = localPlaylist.tracks;
                      this.localVideos.clear();
                      this.videoList.forEach(video => {
                        this.localVideos.set(video.id, true);
                      });

                      if (this.videoList.length > 0) {
                        this.currentVideoIdx = 0;
                        this.currentVideoId = this.videoList[this.currentVideoIdx].id;
                        this.currentVideoName = this.formatVideoName(this.videoList[this.currentVideoIdx].name);
                        this.getVideoTrack().then((value) => {
                          this.trackPath = value;
                          this.videoWaiting = false;
                        });
                      }
                    })
                    .catch((err) => {
                      console.error("Failed to load local playlist", err);
                      this.videoWaiting = false;
                    });
                } else {
                  this.videoWaiting = false;
                }
              })
              .catch(() => {
                this.videoWaiting = false;
              });
          } else {
            // Load from server API (mobile)
            const tokenQuery = await Preferences.get({ key: "jwt" });
            const token = tokenQuery.value;

            const options = {
              url: `${process.env.VUE_APP_API_URL}api/releases/${this.releaseEntity.id}/media/video`,
              headers: { Authorization: `Bearer ${token}` },
              readTimeout: 60000,
            };

            await CapacitorHttp.get(options)
              .then(async (response) => {
                this.videoList = response.data;
                await this.checkLocalVideos();

                if (this.videoList.length > 0) {
                  this.currentVideoIdx = 0;
                  this.currentVideoId = this.videoList[this.currentVideoIdx].id;
                  this.currentVideoName = this.formatVideoName(this.videoList[this.currentVideoIdx].name);
                  this.getVideoTrack().then((value) => {
                    this.trackPath = value;
                    this.videoWaiting = false;
                  });
                } else {
                  this.videoWaiting = false;
                }
              })
              .catch(() => {
                this.$router.replace("/badconnection");
              });
          }
        } else {
          // Load from server API (web)
          axios
            .get(`/api/releases/${this.releaseEntity.id}/media/video`)
            .then((response) => {
              this.videoList = response.data;
              if (this.videoList.length > 0) {
                this.currentVideoIdx = 0;
                this.currentVideoId = this.videoList[this.currentVideoIdx].id;
                this.currentVideoName = this.formatVideoName(this.videoList[this.currentVideoIdx].name);
                this.getVideoTrack().then((value) => {
                  this.trackPath = value;
                  this.videoWaiting = false;
                });
              } else {
                this.videoWaiting = false;
              }
            })
            .catch((err) => {
              console.log("call error " + err);
            });
        }
        return;
      }

      // Original Google Drive logic (for backward compatibility)
      if (this.isMobile) {
        // In offline mode, load only downloaded videos from local playlist
        if (this.globalContext.offlineMode) {
          //load offline video
          console.log(
            "Offline mode: Loading videos from local playlist"
          );

          Filesystem.readdir({
            path: this.releaseEntity.videoDirectory,
            directory: Directory.Data,
          })
            .then((res) => {
              const foundPlaylist = res.files.find((f) => {
                return f.name == this.fsUtils.PLAYLIST_PATH;
              });

              if (
                typeof foundPlaylist !== "undefined" &&
                null != foundPlaylist
              ) {
                Filesystem.readFile({
                  path: `${this.releaseEntity.videoDirectory}/${this.fsUtils.PLAYLIST_PATH}`,
                  directory: Directory.Data,
                  encoding: Encoding.UTF8,
                })
                  .then((res) => {
                    const localPlaylist: Playlist = typeof res.data === 'string' ? JSON.parse(res.data) : null;
                    if (!localPlaylist) throw new Error("Local playlist is null");

                    this.videoList = localPlaylist.tracks;
                    // Mark all videos in playlist as locally available
                    this.localVideos.clear();
                    this.videoList.forEach(video => {
                      this.localVideos.set(video.id, true);
                    });

                    if (this.videoList.length > 0) {
                      this.currentVideoIdx = 0;
                      this.currentVideoId =
                        this.videoList[this.currentVideoIdx].id;
                      this.currentVideoName = this.formatVideoName(this.videoList[this.currentVideoIdx].name);
                      this.getVideoTrack().then((value) => {
                        this.trackPath = value;
                        this.videoWaiting = false;
                      });
                    }
                  })
                  .catch((err) => {
                    console.error("Failed to load local playlist", err);
                    this.videoWaiting = false;
                  });
              } else {
                this.videoWaiting = false;
              }
            })
            .catch(() => {
              // not exists or not a dir
              this.videoWaiting = false;
            });
        } else {
          // In online mode, always load full list from server
          // but check which videos are downloaded locally
          const tokenQuery = await Preferences.get({ key: "jwt" });
          const token = tokenQuery.value;

          const options = {
            url: `${process.env.VUE_APP_API_URL}api/google/media/${this.releaseEntity.programShortName +
              "_" +
              this.releaseEntity.releaseWithZero +
              "_Video"
              }`,
            headers: { Authorization: `Bearer ${token}` },
            readTimeout: 60000,
          };

          await CapacitorHttp.get(options)
            .then(async (response) => {
              // Load full video list from server
              this.videoList = response.data;
              
              // Check which videos are downloaded locally
              await this.checkLocalVideos();
              
              if (this.videoList.length > 0) {
                this.currentVideoIdx = 0;
                this.currentVideoId = this.videoList[this.currentVideoIdx].id;
                this.currentVideoName = this.formatVideoName(this.videoList[this.currentVideoIdx].name);
                this.getVideoTrack().then((value) => {
                  this.trackPath = value;
                  this.videoWaiting = false;
                });
              } else {
                this.videoWaiting = false;
              }
            })
            .catch(() => {
              this.$router.replace("/badconnection");
            });
        }
      } else {
        axios
          .get(
            `/api/google/media/${this.releaseEntity.programShortName +
            "_" +
            this.releaseEntity.releaseWithZero +
            "_Video"
            }`
          )
          .then((response) => {
            this.videoList = response.data;
            if (this.videoList.length > 0) {
              this.currentVideoIdx = 0;
              this.currentVideoId = this.videoList[this.currentVideoIdx].id;
              this.currentVideoName = this.formatVideoName(this.videoList[this.currentVideoIdx].name);
              this.getVideoTrack().then((value) => {
                this.trackPath = value;
                this.videoWaiting = false;
              });
            } else {
              this.videoWaiting = false;
            }
          })
          .catch((err) => {
            console.log("call error " + err);
          });
      }
    },
    async checkLocalVideos(): Promise<void> {
      // Check which videos from the list are downloaded locally
      this.localVideos.clear();
      
      try {
        const dirContent = await Filesystem.readdir({
          path: this.releaseEntity.videoDirectory,
          directory: Directory.Data,
        });

        for (const video of this.videoList) {
          const fileExtension = this.mimeExtensions.get(video.mimeType || '') || 'mp4';
          const fileName = `${video.id}.${fileExtension}`;
          
          const fileExists = dirContent.files.some(f => 
            f.name === fileName && !f.name.startsWith('dwn_') && f.size > 0
          );
          
          if (fileExists) {
            this.localVideos.set(video.id, true);
          }
        }
      } catch (error) {
        // Directory doesn't exist or error reading - no local videos
        console.log('No local video directory or error reading:', error);
      }
    },

    async getVideoTrack(): Promise<string> {
      const tokenQuery = await Preferences.get({ key: "jwt" });
      const token = tokenQuery.value;

      return new Promise<string>((resolve) => {
        if (this.videoList.length < 1) return resolve("");
        this.currentVideoId = this.videoList[this.currentVideoIdx].id;
        this.currentVideoName = this.formatVideoName(this.videoList[this.currentVideoIdx].name);

        if (this.isMobile && this.globalContext.offlineMode) {
          // In offline mode, always use local file
          Filesystem.getUri({
            path: `${this.releaseEntity.videoDirectory}/${this.currentVideoId
              }.${this.mimeExtensions.get(
                this.videoList[this.currentVideoIdx].mimeType
              )}`,
            directory: Directory.Data,
          }).then((res) => {
            resolve(`${Capacitor.convertFileSrc(res.uri)}`);
          }).catch(() => {
            resolve("");
          });
        } else if (this.isMobile && this.localVideos.has(this.currentVideoId)) {
          // In online mode, if video is downloaded locally, use local file
          Filesystem.getUri({
            path: `${this.releaseEntity.videoDirectory}/${this.currentVideoId
              }.${this.mimeExtensions.get(
                this.videoList[this.currentVideoIdx].mimeType
              )}`,
            directory: Directory.Data,
          }).then((res) => {
            resolve(`${Capacitor.convertFileSrc(res.uri)}`);
          }).catch(() => {
            // If local file access fails, fallback to remote
            if (token) {
              this.getRemoteVideoTrack(token).then(resolve);
            } else {
              resolve("");
            }
          });
        } else {
          // In online mode, use remote video
          if (token) {
            this.getRemoteVideoTrack(token).then(resolve);
          } else {
            resolve("");
          }
        }
      });
    },

    async getRemoteVideoTrack(token: string): Promise<string> {
      return new Promise<string>((resolve) => {
        // Check if video is from server (path starts with "releases/")
        const isServerVideo = this.currentVideoId.startsWith('releases/');

        if (isServerVideo) {
          // Use server API for releases
          const options = {
            url: `${process.env.VUE_APP_API_URL}api/releases/stream-request?path=${encodeURIComponent(this.currentVideoId)}`,
            headers: { Authorization: `Bearer ${token}` },
            readTimeout: 60000,
          };

          this.videoWaiting = true;

          CapacitorHttp.get(options)
            .then((response) => {
              if (response.data && response.data.streamUrl) {
                resolve(response.data.streamUrl);
              } else {
                // Fallback to direct streaming
                resolve(`${process.env.VUE_APP_API_URL}api/releases/stream?path=${encodeURIComponent(this.currentVideoId)}`);
              }
            })
            .catch(() => {
              // Fallback to direct streaming
              resolve(`${process.env.VUE_APP_API_URL}api/releases/stream?path=${encodeURIComponent(this.currentVideoId)}`);
            });
        } else {
          // Original Google Drive logic
          const options = {
            url: `${process.env.VUE_APP_API_URL}api/google/video/storage/${this.currentVideoId}`,
            headers: { Authorization: `Bearer ${token}` },
            readTimeout: 60000,
          };

          this.videoWaiting = true;

          CapacitorHttp.get(options)
            .then((response) => {
              if (response.data) {
                const trackPath = this.videoList[this.currentVideoIdx].m3u
                  ? `${process.env.VUE_APP_MEDIA_URL}media/${this.currentVideoId}-ffmpeg/index.m3u8`
                  : `${process.env.VUE_APP_VIDEO_URL}?id=${this.currentVideoId}${this.videoQuality !== 'hq' ? '-' + this.videoQuality + '.mp4' : ''}`;

                resolve(trackPath);
              } else {
                resolve("");
              }
            })
            .catch(() => {
              this.$router.replace("/badconnection");
              resolve("");
            });
        }
      });
    },
    onSelectVideoTrack() {
      this.videoRef.pause();
      this.restartVideoPlayer();
    },
    nextTrack() {
      if (this.currentVideoIdx === this.videoList.length - 1) return;

      ++this.currentVideoIdx;
      if (this.currentVideoIdx > this.videoList.length - 1) {
        --this.currentVideoIdx;
      }
      this.currentVideoId = this.videoList[this.currentVideoIdx].id;
      this.currentVideoName = this.formatVideoName(this.videoList[this.currentVideoIdx].name);
      this.restartVideoPlayer();
    },
    prevTrack() {
      if (this.currentVideoIdx < 1) return;

      --this.currentVideoIdx;
      if (this.currentVideoIdx < 0) {
        ++this.currentVideoIdx;
      }
      this.currentVideoId = this.videoList[this.currentVideoIdx].id;
      this.currentVideoName = this.formatVideoName(this.videoList[this.currentVideoIdx].name);
      this.restartVideoPlayer();
    },
    restartVideoPlayer() {
      this.setCurrentTime(0);
      this.getVideoTrack().then((value) => {
        this.trackPath = value;
        this.videoRef.load();
      });
      if (this.globalContext.syncVideoGuide && this.links.length > 0) {
        this.scrollTop(this.links[this.currentVideoIdx].destPage);
      }
    },
    playerControls(play: boolean) {
      if (!this.videoRef) return;
      this.videoPlaying = play;
      this.continuePlaying = play;
      if (play) {
        if (this.videoRef.currentTime > 0) {
          this.videoRef.play();
        } else {
          this.restartVideoPlayer();
        }
      } else {
        this.videoRef.pause();
      }
    },
    changeCurrentTime(delta: number): void {
      if (!this.videoRef) return;
      this.videoRef.currentTime = this.videoRef.currentTime + delta;
      if (delta > 0) {
        this.initBackdropTimeout();
        clearTimeout(rewindTimeout);
        this.isPlayForward = true;
        rewindTimeout = setTimeout(() => {
          this.isPlayForward = false;
        }, 1000);
      } else {
        this.initBackdropTimeout();
        clearTimeout(rewindTimeout);
        this.isPlayBack = true;
        rewindTimeout = setTimeout(() => {
          this.isPlayBack = false;
        }, 1000);
      }
    },
    setCurrentTime(value: number): void {
      if (!this.videoRef) return;
      this.playerRangeCurrentValue = value;
      this.videoRef.currentTime = value;
    },
    showControls() {
      this.showBackdrop = !this.showBackdrop;
      if (this.showBackdrop) {
        this.initBackdropTimeout();
      } else {
        clearTimeout(backdropTimeout);
      }
    },
    initBackdropTimeout() {
      clearTimeout(backdropTimeout);
      backdropTimeout = setTimeout(() => {
        if (this.videoPlaying && !this.videoWaiting) {
          this.showBackdrop = false;
        }
      }, 5000);
    },
    rangeDragging(value: string) {
      this.currentTrackCurrentTime = this.mediaUtils.formatTime(Number(value));
    },
    rangeDragStart() {
      if (!this.videoRef) return;
      this.videoRef.pause();
    },
    rangeDragEnd() {
      if (!this.videoRef) return;
      this.videoRef.currentTime = this.playerRangeCurrentValue;
      this.currentTrackCurrentTime = this.mediaUtils.formatTime(
        this.playerRangeCurrentValue
      );
      this.videoRef.play();
    },

    async loadProgramLogo() {
      if (this.releaseEntity.programShortName) {
        try {
          this.programLogo = await this.logoService.getProgramLogo(this.releaseEntity.programShortName);
        } catch (error) {
          console.error(`Failed to load logo for ${this.releaseEntity.programShortName}:`, error);
        }
      }
    },

    getProgramImage(shortName: string | undefined): string {
      if (!shortName || shortName.trim() === '') {
        return require('@/theme/liga-logo-white.png');
      }
      
      try {
        return require(`@/theme/programs/${shortName}.png`);
      } catch (error) {
        console.warn(`Program image not found for ${shortName}, using Liga logo as fallback`);
        return require('@/theme/liga-logo-white.png');
      }
    },
  },
});

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "acb5df0a": (_ctx.buffered)
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__